import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Package, ShoppingCart, AlertCircle, Info, X, Loader2, Trash2, CheckCircle, Calendar, Clock } from 'lucide-react';
import { fetchOrderForm, createOrder } from '../lib/api';
import type { Product } from '../lib/types';

interface OrderItem {
  id: string;
  product: Product;
  quantity: number;
  discount: number;
}

interface OrderForm {
  id: string;
  customerId: string;
  customerName: string;
  pricingTiers: Array<{
    id: string;
    minQuantity: number;
    discountPercentage: number;
  }>;
  expiresAt: string;
}

const formatCurrency = (amount: number): string => {
  return amount.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

const ViewOrder: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [quantityNotices, setQuantityNotices] = useState<Record<string, string>>({});
  const [orderForm, setOrderForm] = useState<OrderForm | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [cart, setCart] = useState<OrderItem[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [createdOrderId, setCreatedOrderId] = useState<string | null>(null);
  const [enlargedImage, setEnlargedImage] = useState<string | null>(null);

  useEffect(() => {
    loadOrderForm();
  }, [id]);

  const loadOrderForm = async () => {
    if (!id) return;

    try {
      setLoading(true);
      const data = await fetchOrderForm(id);
      setOrderForm(data.orderForm);
      setProducts(data.products);
      setError(null);
    } catch (err) {
      setError('Failed to load order form');
      console.error('Error loading order form:', err);
    } finally {
      setLoading(false);
    }
  };

  const categories = [
    { id: 'all', name: 'All Products', count: products.filter(p => Number(p.qoh) > 0).length },
    { id: 'Concentrate', name: 'Concentrates', count: products.filter(p => p.category === 'Concentrate' && Number(p.qoh) > 0).length },
    { id: 'Cartridge', name: 'Cartridges', count: products.filter(p => p.category === 'Cartridge' && Number(p.qoh) > 0).length },
    { id: 'Edible', name: 'Edibles', count: products.filter(p => p.category === 'Edible' && Number(p.qoh) > 0).length },
    { id: 'Flower', name: 'Flower', count: products.filter(p => p.category === 'Flower' && Number(p.qoh) > 0).length },
    { id: 'Preroll', name: 'Prerolls', count: products.filter(p => p.category === 'Preroll' && Number(p.qoh) > 0).length }
  ];

  const filteredProducts = products.filter(product => 
    (selectedCategory === 'all' || product.category === selectedCategory) &&
    Number(product.qoh) > 0
  );

  const sortedFilteredProducts = filteredProducts.sort((a, b) => 
    a.product.localeCompare(b.product)
  );

  const calculateDiscount = (quantity: number): number => {
    if (!orderForm?.pricingTiers?.length) return 0;
    
    // Only consider tiers that have both minQuantity and discountPercentage
    const validTiers = orderForm.pricingTiers.filter(tier => 
      tier.minQuantity > 0 && tier.discountPercentage > 0
    );
    
    if (validTiers.length === 0) return 0;
    
    const applicableTier = [...validTiers]
      .sort((a, b) => b.minQuantity - a.minQuantity)
      .find(tier => quantity >= tier.minQuantity);
    
    return applicableTier?.discountPercentage || 0;
  };

  const handleQuantityChange = (product: Product, requestedQuantity: number) => {
    const quantity = Math.max(0, requestedQuantity);
    
    if (quantity > Number(product.qoh)) {
      const actualQuantity = Number(product.qoh);
      setQuantityNotices({
        ...quantityNotices,
        [product.id]: `Only ${product.qoh} units available. ${product.qoh} units have been added to your order.`
      });
      updateCart(product, actualQuantity);
      return;
    }

    setQuantityNotices(prev => {
      const updated = { ...prev };
      delete updated[product.id];
      return updated;
    });

    updateCart(product, quantity);
  };

  const updateCart = (product: Product, quantity: number) => {
    setCart(prevCart => {
      if (quantity === 0) {
        return prevCart.filter(item => item.id !== product.id);
      }

      const discount = calculateDiscount(quantity);
      const existingItem = prevCart.find(item => item.id === product.id);
      
      if (existingItem) {
        return prevCart.map(item =>
          item.id === product.id
            ? { ...item, quantity, discount }
            : item
        );
      }
      
      return [...prevCart, { id: product.id, product, quantity, discount }];
    });
  };

  const removeFromCart = (productId: string) => {
    setCart(prevCart => prevCart.filter(item => item.id !== productId));
    setQuantityNotices(prev => {
      const updated = { ...prev };
      delete updated[productId];
      return updated;
    });
  };

  const calculateItemTotal = (item: OrderItem): number => {
    const itemTotal = parseFloat(item.product.price) * item.quantity;
    const discount = itemTotal * (item.discount / 100);
    return itemTotal - discount;
  };

  const subtotal = cart.reduce((sum, item) => sum + (parseFloat(item.product.price) * item.quantity), 0);
  const totalDiscount = cart.reduce((sum, item) => sum + (parseFloat(item.product.price) * item.quantity * (item.discount / 100)), 0);
  const total = subtotal - totalDiscount;

  const getTotalUnits = (items: OrderItem[]): number => {
    return items.reduce((total, item) => total + item.quantity, 0);
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const totalUnits = getTotalUnits(cart);

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Order Details - ${orderForm?.customerName}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            table { width: 100%; border-collapse: collapse; margin: 20px 0; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f8f8f8; }
            .header { margin-bottom: 30px; }
            .total { margin-top: 20px; text-align: right; font-weight: bold; }
            .footer { margin-top: 40px; border-top: 1px solid #ddd; padding-top: 20px; }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Order Details</h1>
            <p>Customer: ${orderForm?.customerName}</p>
            <p>Order Date: ${new Date().toLocaleDateString()}</p>
            <p>Total Units: ${totalUnits}</p>
          </div>
          <table>
            <thead>
              <tr>
                <th>Product</th>
                <th>SKU</th>
                <th>Unit Size</th>
                <th>Quantity</th>
                <th>Price</th>
                <th>Discount</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              ${cart.map(item => {
                const itemTotal = parseFloat(item.product.price) * item.quantity;
                const discount = itemTotal * (item.discount / 100);
                const total = itemTotal - discount;
                return `
                  <tr>
                    <td>${item.product.product}</td>
                    <td>${item.product.sku}</td>
                    <td>${item.product.unitSize || '-'}</td>
                    <td>${item.quantity}</td>
                    <td>${formatCurrency(parseFloat(item.product.price))}</td>
                    <td>${item.discount}%</td>
                    <td>${formatCurrency(total)}</td>
                  </tr>
                `;
              }).join('')}
            </tbody>
          </table>
          <div class="total">
            <p>Subtotal: ${formatCurrency(subtotal)}</p>
            <p>Total Discount: ${formatCurrency(totalDiscount)}</p>
            <p>Total: ${formatCurrency(total)}</p>
          </div>
          <div class="footer">
            <p>Sticky Saguaro Wholesale</p>
            <p>12338 E Riggs Road, Chandler AZ 85249</p>
          </div>
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.print();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!orderForm) {
      setError('Order form not found');
      return;
    }

    if (cart.length === 0) {
      setError('Please add at least one item to your order');
      return;
    }
    
    try {
      setSubmitting(true);
      setError(null);

      const orderData = {
        orderFormId: orderForm.id,
        customerId: orderForm.customerId,
        items: cart.map(item => ({
          productId: item.product.id,
          quantity: item.quantity,
          price: parseFloat(item.product.price),
          discountPercentage: item.discount
        }))
      };

      const response = await createOrder(orderData);
      setCreatedOrderId(response.id);
      setShowConfirmation(true);
    } catch (error) {
      console.error('Error submitting order:', error);
      setError('Failed to submit order. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-green-600 animate-spin" />
      </div>
    );
  }

  if (error || !orderForm) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Error Loading Order Form</h2>
          <p className="text-gray-600">{error || 'Order form not found'}</p>
          <button
            onClick={loadOrderForm}
            className="mt-4 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Check if there are any valid pricing tiers (with both minQuantity and discountPercentage > 0)
  const hasValidPricingTiers = orderForm.pricingTiers.some(tier => 
    tier.minQuantity > 0 && tier.discountPercentage > 0
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-6">
              <div className="p-4">
                <img 
                  src="https://web.twistedvoip.com/Sticky-Logo_Handcrafted-Experience.jpg" 
                  alt="Sticky Saguaro Logo" 
                  className="h-16"
                />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Wholesale Order Form</h1>
                <p className="text-gray-600">{orderForm.customerName}</p>
              </div>
            </div>
            <div className="text-sm text-gray-600">
              Order Form ID: {orderForm.id}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex gap-8">
          <div className="w-80 flex-shrink-0">
            <div className="sticky top-8 space-y-6">
              <div className="bg-white rounded-lg shadow-sm p-4">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Categories</h2>
                <div className="space-y-2">
                  {categories.map(category => (
                    <button
                      key={category.id}
                      onClick={() => setSelectedCategory(category.id)}
                      className={`w-full flex items-center justify-between px-4 py-2 rounded-lg transition-colors ${
                        selectedCategory === category.id
                          ? 'bg-green-100 text-green-800'
                          : 'hover:bg-gray-50'
                      }`}
                    >
                      <span>{category.name}</span>
                      <span className="text-sm text-gray-500">{category.count}</span>
                    </button>
                  ))}
                </div>
              </div>

              {cart.length > 0 && (
                <div className="bg-white rounded-lg shadow-sm p-4">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">Order Summary</h2>
                  <div className="space-y-4">
                    {cart.map((item) => {
                      const itemTotal = calculateItemTotal(item);
                      return (
                        <div key={item.id} className="flex justify-between items-start">
                          <div className="flex-1">
                            <p className="font-medium text-gray-900">{item.product.product}</p>
                            <p className="text-sm text-gray-600">
                              {item.quantity} × {formatCurrency(parseFloat(item.product.price))}
                              {item.discount > 0 && (
                                <span className="text-green-600 ml-2">
                                  (-{item.discount}%)
                                </span>
                              )}
                            </p>
                          </div>
                          <div className="flex items-center space-x-4">
                            <p className="font-medium text-gray-900">{formatCurrency(itemTotal)}</p>
                            <button
                              onClick={() => removeFromCart(item.id)}
                              className="text-red-500 hover:text-red-700"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                    
                    <div className="border-t pt-4">
                      <div className="flex justify-between text-gray-600">
                        <span>Subtotal</span>
                        <span>{formatCurrency(subtotal)}</span>
                      </div>
                      {totalDiscount > 0 && (
                        <div className="flex justify-between text-green-600 mt-2">
                          <span>Volume Discount</span>
                          <span>-{formatCurrency(totalDiscount)}</span>
                        </div>
                      )}
                      <div className="flex justify-between text-lg font-bold text-gray-900 mt-2">
                        <span>Total</span>
                        <span>{formatCurrency(total)}</span>
                      </div>
                    </div>

                    <button
                      onClick={handleSubmit}
                      disabled={submitting}
                      className={`w-full mt-4 bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 transition duration-200 flex items-center justify-center space-x-2 ${
                        submitting ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                    >
                      {submitting ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <>
                          <ShoppingCart className="w-5 h-5" />
                          <span>Submit Order</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}

              {hasValidPricingTiers && (
                <div className="bg-white rounded-lg shadow-sm p-4">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">Volume Discounts</h2>
                  <div className="space-y-2">
                    {orderForm.pricingTiers
                      .filter(tier => tier.minQuantity > 0 && tier.discountPercentage > 0)
                      .sort((a, b) => a.minQuantity - b.minQuantity)
                      .map((tier) => (
                        <div
                          key={tier.id}
                          className="flex items-center justify-between p-2 rounded-lg bg-gray-50"
                        >
                          <span className="text-gray-700">{tier.minQuantity}+ units</span>
                          <span className="font-medium text-green-600">{tier.discountPercentage}% off</span>
                        </div>
                      ))
                    }
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="flex-1">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {sortedFilteredProducts.map((product) => {
                const cartItem = cart.find(item => item.id === product.id);
                const isInCart = Boolean(cartItem);
                const notice = quantityNotices[product.id];
                
                return (
                  <div 
                    key={product.id} 
                    className={`bg-white rounded-lg shadow-sm p-6 flex flex-col relative ${
                      isInCart ? 'ring-2 ring-green-500' : ''
                    }`}
                  >
                    {isInCart && (
                      <div className="absolute top-2 right-2 bg-green-100 text-green-800 px-2 py-1 rounded-full flex items-center text-sm font-medium">
                        <CheckCircle className="w-4 h-4 mr-1" />
                        In Cart
                      </div>
                    )}
                    <div className="mb-4">
                      <div className="flex flex-col">
                        <div className="flex items-start justify-between mb-2">
                          <h3 className="text-xl font-semibold text-gray-900 line-clamp-2 min-h-[3.5rem]">
                            {product.product}
                          </h3>
                        </div>
                        <div className="text-sm text-gray-600">
                          <p><span className="font-medium">Brand:</span> {product.brand}</p>
                          <p><span className="font-medium">SKU:</span> {product.sku}</p>
                          <p><span className="font-medium">Batch:</span> {product.batchNumber}</p>
                          <p><span className="font-medium">Unit Size:</span> {product.unitSize}</p>
                          {product.testingResults && (
                            <a
                              href={product.testingResults}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-block text-sm text-green-600 hover:text-green-700"
                            >
                              View Testing Results
                            </a>
                          )}
                          <p><span className="font-medium">Category:</span> {product.category}</p>
                        </div>
                      </div>
                    </div>

                    {product.images && product.images.length > 0 && (
                      <div className="mb-4">
                        <div className="grid grid-cols-2 gap-2">
                          {product.images.map((image, index) => (
                            <img
                              key={index}
                              src={image}
                              alt={`${product.product} - Image ${index + 1}`}
                              className="w-full h-32 object-contain rounded-lg bg-white cursor-pointer hover:opacity-80 transition-opacity border border-gray-200"
                              onClick={() => setEnlargedImage(image)}
                            />
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="flex-1 space-y-4">
                      <div className="space-y-1 text-sm text-gray-600">
                        <p><span className="font-medium">Strain:</span> {product.strain}</p>
                        {product.lineage && (
                          <p><span className="font-medium">Lineage:</span> {product.lineage}</p>
                        )}
                        <p><span className="font-medium">Dominance:</span> {product.dominance}</p>
                        <p><span className="font-medium">THC:</span> {product.totalThc}{product.category === 'Edible' ? 'mg' : '%'}</p>
                        <p><span className="font-medium">CBD:</span> {product.totalCbd}{product.category === 'Edible' ? 'mg' : '%'}</p>
                        <p><span className="font-medium">Total Cannabinoids:</span> {product.totalCannabinoids}{product.category === 'Edible' ? 'mg' : '%'}</p>
                        <p><span className="font-medium">Available:</span> {product.qoh} units</p>
                      </div>

                      <div className="pt-4">
                        <div className="flex items-center justify-between mb-1">
                          <label className="block text-sm font-medium text-gray-700">
                            Quantity
                          </label>
                          <span className="text-lg font-bold text-green-600">
                            {formatCurrency(parseFloat(product.price))}
                          </span>
                        </div>
                        <div className="space-y-2">
                          <input
                            type="number"
                            min="0"
                            max={product.qoh}
                            value={cartItem?.quantity || ''}
                            onChange={(e) => handleQuantityChange(product, parseInt(e.target.value) || 0)}
                            className={`w-full px-3 py-2 border rounded-md focus:ring-green-500 focus:border-green-500 ${
                              notice ? 'border-yellow-300' : ''
                            }`}
                            placeholder="Enter quantity"
                          />
                          {notice && (
                            <p className="text-sm text-yellow-600 flex items-center">
                              <Info className="w-4 h-4 mr-1" />
                              {notice}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {enlargedImage && (
        <div
          className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center p-4 z-50"
          onClick={() => setEnlargedImage(null)}
        >
          <div className="relative max-w-5xl max-h-[90vh]">
            <button
              onClick={() => setEnlargedImage(null)}
              className="absolute -top-12 right-0 text-white hover:text-gray-300 transition-colors"
            >
              <X className="w-8 h-8" />
            </button>
            <img
              src={enlargedImage}
              alt="Enlarged product"
              className="max-w-full max-h-[90vh] object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}

      {showConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl p-8 max-w-md w-full transform transition-all">
            <div className="flex flex-col items-center text-center">
              <div className="bg-green-100 p-3 rounded-full mb-4">
                <CheckCircle className="w-12 h-12 text-green-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Order Submitted Successfully!</h3>
              <p className="text-gray-600 mb-6">
                Thank you for your order. We'll begin processing it right away.
              </p>
              <div className="bg-gray-50 w-full rounded-lg p-4 mb-6">
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-gray-600">Order Number:</span>
                  <span className="font-medium text-gray-900">{createdOrderId}</span>
                </div>
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-gray-600">Total Units:</span>
                  <span className="font-medium text-gray-900">{getTotalUnits(cart)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Total Amount:</span>
                  <span className="font-bold text-green-600">{formatCurrency(total)}</span>
                </div>
              </div>
              <div className="flex flex-col w-full gap-3">
                <button
                  onClick={() => window.location.href = 'https://stickysaguaro.com'}
                  className="w-full bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 transition duration-200"
                >
                  Close Window
                </button>
                <button
                  onClick={handlePrint}
                  className="w-full border border-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-50 transition duration-200"
                >
                  Print Order Details
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="bg-black mt-8">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400">
                Questions? Contact our wholesale team:
              </p>
              <a 
                href="mailto:wholesale@stickysaguaro.com" 
                className="text-white hover:text-green-400 transition-colors"
              >
                wholesale@stickysaguaro.com
              </a>
            </div>
            <div className="text-right">
              <p className="text-gray-400">WASP Wholesale Manager V2</p>
              <p className="text-gray-400">© 2025-{new Date().getFullYear()} - Sticky Saguaro</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewOrder;